import React, { useState, useEffect } from 'react';
import { Room } from 'livekit-client';
import { toast } from 'react-toastify';
import { collection, addDoc, query, orderBy, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../../Config/firebase';

interface PollOption {
    id: string;
    text: string;
    votes: number;
}

interface Poll {
    id?: string;
    question: string;
    options: PollOption[];
    type: 'poll' | 'quiz';
    correctAnswer?: string; // For quizzes only
    active: boolean;
    createdAt: any;
    createdBy: string;
    responses?: Map<string, string>; // studentName -> selectedOptionId
}

interface StudentResponse {
    studentName: string;
    selectedOption: string;
    timestamp: number;
    isCorrect?: boolean;
}

interface PollsQuizzesProps {
    subscriptionId: string;
    subscriptionName: string;
    userRole?: string;
    isInMeeting?: boolean;
    participantName?: string;
    room?: Room | null;
    serviceProviderId?: string;
}

const PollsQuizzes: React.FC<PollsQuizzesProps> = ({
    subscriptionId,
    userRole,
    isInMeeting = false,
    participantName = 'User',
    room = null,
    serviceProviderId
}) => {
    const [activePoll, setActivePoll] = useState<Poll | null>(null);
    const [responses, setResponses] = useState<Map<string, StudentResponse>>(new Map());
    const [myResponse, setMyResponse] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [savedPolls, setSavedPolls] = useState<Poll[]>([]);
    const [resultsRevealed, setResultsRevealed] = useState(false);
    const [pollResults, setPollResults] = useState<any>(null);
    const [deletingPollId, setDeletingPollId] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        question: '',
        type: 'poll' as 'poll' | 'quiz',
        options: ['', '', '', ''],
        correctAnswer: ''
    });

    // Load saved polls from Firestore
    useEffect(() => {
        if (!serviceProviderId || !subscriptionId) return;
        loadSavedPolls();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceProviderId, subscriptionId]);

    const loadSavedPolls = async () => {
        if (!serviceProviderId || !subscriptionId) return;
        try {
            const pollsRef = collection(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscriptionId}/PollsQuizzes`);
            const q = query(pollsRef, orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            const polls = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Poll[];
            setSavedPolls(polls);
        } catch (error) {
            console.error('Error loading polls:', error);
        }
    };

    // Note: Metadata checking removed due to permission restrictions
    // Late joiners will need to wait for the teacher to restart the poll

    // Listen for poll/quiz broadcasts
    useEffect(() => {
        if (!room || !isInMeeting) return;

        const handleData = (payload: Uint8Array) => {
            try {
                const decoder = new TextDecoder();
                const message = JSON.parse(decoder.decode(payload));

                // Poll started
                if (message.type === 'poll' && message.action === 'POLL_STARTED') {
                    if (userRole !== 'teacher') {
                        console.log('📊 Student received POLL_STARTED:', message.poll.question);
                        // Only reset if it's a different poll
                        if (activePoll?.id !== message.poll.id) {
                            setActivePoll(message.poll);
                            setMyResponse(null);
                            setResultsRevealed(false);
                            setPollResults(null);
                            toast.success(`📊 ${message.poll.type === 'poll' ? 'Poll' : 'Quiz'} started: ${message.poll.question}`);
                        } else {
                            console.log('📊 Same poll already active, ignoring duplicate start');
                        }
                    }
                    return;
                }

                // Poll stopped
                if (message.type === 'poll' && message.action === 'POLL_STOPPED') {
                    if (userRole !== 'teacher') {
                        setActivePoll(null);
                        setMyResponse(null);
                        setResultsRevealed(false);
                        setPollResults(null);
                        toast.info('Poll ended');
                    }
                    return;
                }

                // Results revealed
                if (message.type === 'poll' && message.action === 'RESULTS_REVEALED') {
                    if (userRole !== 'teacher') {
                        setResultsRevealed(true);
                        setPollResults(message.results);
                        console.log('📊 Student received full results:', message.results);
                        toast.success('📊 Teacher revealed the results!');
                    }
                    return;
                }

                // Student response (only teacher receives)
                if (message.type === 'poll' && message.action === 'STUDENT_RESPONSE') {
                    if (userRole === 'teacher') {
                        setResponses(prev => {
                            const newMap = new Map(prev);
                            newMap.set(message.studentName, {
                                studentName: message.studentName,
                                selectedOption: message.selectedOption,
                                timestamp: message.timestamp,
                                isCorrect: message.isCorrect
                            });
                            return newMap;
                        });
                        console.log(`📊 ${message.studentName} responded`);
                    }
                    return;
                }
            } catch (error) {
                console.error('Error parsing poll data:', error);
            }
        };

        room.on('dataReceived', handleData);
        return () => {
            room.off('dataReceived', handleData);
        };
    }, [room, isInMeeting, userRole]);

    // Create and save poll
    const createPoll = async () => {
        if (!formData.question.trim()) {
            toast.error('Please enter a question');
            return;
        }

        const validOptions = formData.options.filter(opt => opt.trim());
        if (validOptions.length < 2) {
            toast.error('Please provide at least 2 options');
            return;
        }

        if (formData.type === 'quiz' && !formData.correctAnswer) {
            toast.error('Please select the correct answer for quiz');
            return;
        }

        const poll: any = {
            question: formData.question,
            type: formData.type,
            options: validOptions.map((text, idx) => ({
                id: `opt_${idx}`,
                text,
                votes: 0
            })),
            active: false,
            createdAt: serverTimestamp(),
            createdBy: participantName
        };

        // Only add correctAnswer for quizzes (Firestore doesn't accept undefined)
        if (formData.type === 'quiz') {
            poll.correctAnswer = formData.correctAnswer;
        }

        try {
            // Save to Firestore
            if (serviceProviderId && subscriptionId) {
                const pollsRef = collection(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscriptionId}/PollsQuizzes`);
                await addDoc(pollsRef, poll);
                toast.success(`${formData.type === 'poll' ? 'Poll' : 'Quiz'} created and saved!`);
                loadSavedPolls();
            }

            setShowCreateModal(false);
            resetForm();
        } catch (error) {
            console.error('Error creating poll:', error);
            toast.error('Failed to create poll');
        }
    };

    // Delete poll
    const deletePoll = async (pollId: string) => {
        if (!serviceProviderId || !subscriptionId) return;
        
        try {
            setDeletingPollId(pollId);
            const pollRef = doc(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscriptionId}/PollsQuizzes/${pollId}`);
            await deleteDoc(pollRef);
            toast.success('Poll/Quiz deleted successfully');
            loadSavedPolls();
        } catch (error) {
            console.error('Error deleting poll:', error);
            toast.error('Failed to delete poll');
        } finally {
            setDeletingPollId(null);
        }
    };

    // Start poll (broadcast to all students)
    const startPoll = async (poll: Poll) => {
        if (!room || !isInMeeting) {
            toast.error('Not in a live meeting');
            return;
        }

        // Prevent restarting the same poll (would reset student responses)
        if (activePoll?.id === poll.id) {
            toast.warning('This poll is already active!');
            return;
        }

        const pollData = {
            type: 'poll',
            action: 'POLL_STARTED',
            poll: poll,
            timestamp: Date.now()
        };

        try {
            console.log('📊 Starting poll:', poll.question);
            
            // Set local state first
            setActivePoll(poll);
            setResponses(new Map());
            setResultsRevealed(false);
            setPollResults(null);
            
            // Wait longer for room and data channel to be fully ready, then publish
            setTimeout(async () => {
                try {
                    // Check if room and local participant are ready
                    if (!room || !room.localParticipant) {
                        console.error('📊 Room or local participant not ready');
                        toast.error('Room not ready - try rebroadcast button');
                        return;
                    }
                    
                    const encoder = new TextEncoder();
                    const payload = encoder.encode(JSON.stringify(pollData));
                    
                    // Retry mechanism for data channel publish
                    let retryCount = 0;
                    const maxRetries = 3;
                    
                    while (retryCount < maxRetries) {
                        try {
                            await room.localParticipant.publishData(payload, { reliable: true });
                            console.log('📊 Poll data published to data channel successfully');
                            toast.success(`📊 ${poll.type === 'poll' ? 'Poll' : 'Quiz'} started and broadcasted!`);
                            break;
                        } catch (publishError) {
                            retryCount++;
                            console.log(`📊 Poll publish attempt ${retryCount} failed:`, publishError);
                            
                            if (retryCount < maxRetries) {
                                await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
                            } else {
                                console.error('📊 Failed to publish poll after all retries');
                                toast.error('Poll started but may not reach all participants - use rebroadcast button');
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error in poll publish retry:', error);
                }
            }, 2000); // Wait 2 seconds for room and data channel to be fully ready
            
            console.log('📊 Poll state set - activePoll:', poll.id, 'responses cleared');
        } catch (error) {
            console.error('Error starting poll:', error);
            toast.error('Failed to start poll');
        }
    };

    // Reveal results
    const revealResults = async () => {
        if (!room || !isInMeeting || !activePoll) return;

        // Calculate results data to send to students
        const optionCounts = new Map<string, number>();
        activePoll.options.forEach(opt => optionCounts.set(opt.id, 0));
        
        responses.forEach(response => {
            const current = optionCounts.get(response.selectedOption) || 0;
            optionCounts.set(response.selectedOption, current + 1);
        });

        const resultsData = {
            type: 'poll',
            action: 'RESULTS_REVEALED',
            poll: activePoll,
            results: {
                totalResponses: responses.size,
                optionCounts: Object.fromEntries(optionCounts),
                correctAnswer: activePoll.correctAnswer
            },
            timestamp: Date.now()
        };

        try {
            console.log('👁️ Revealing results to students with full data');
            const encoder = new TextEncoder();
            const payload = encoder.encode(JSON.stringify(resultsData));
            await room.localParticipant?.publishData(payload, { reliable: true });
            
            setResultsRevealed(true);
            toast.success('👁️ Results revealed to all students!');
        } catch (error) {
            console.error('Error revealing results:', error);
            toast.error('Failed to reveal results');
        }
    };

    // Stop poll
    const stopPoll = async () => {
        if (!room || !isInMeeting) return;

        const stopData = {
            type: 'poll',
            action: 'POLL_STOPPED',
            timestamp: Date.now()
        };

        try {
            console.log('⏹️ Stopping poll:', activePoll?.question);
            const encoder = new TextEncoder();
            const payload = encoder.encode(JSON.stringify(stopData));
            await room.localParticipant?.publishData(payload, { reliable: true });
            console.log('⏹️ Stop signal broadcasted');
            
            // Note: Metadata updates removed due to permission restrictions
            
            // Save results to Firestore
            if (activePoll?.id && serviceProviderId && subscriptionId) {
                const pollRef = doc(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscriptionId}/PollsQuizzes/${activePoll.id}`);
                const results = Array.from(responses.values());
                
                // Filter out any undefined values from results
                const cleanResults = results.filter(result => result && typeof result === 'object');
                
                await updateDoc(pollRef, {
                    active: false,
                    endedAt: serverTimestamp(),
                    totalResponses: cleanResults.length,
                    results: cleanResults.length > 0 ? cleanResults : []
                });
                console.log('⏹️ Poll results saved to Firestore');
            }
            
            setActivePoll(null);
            setResponses(new Map());
            setResultsRevealed(false);
            setPollResults(null);
            toast.success('⏹️ Poll stopped and results saved!');
        } catch (error) {
            console.error('Error stopping poll:', error);
            toast.error('Failed to stop poll');
        }
    };

    // Submit student response
    const submitResponse = (optionId: string) => {
        if (!activePoll || !room || !isInMeeting) return;

        const selectedOption = activePoll.options.find(opt => opt.id === optionId);
        if (!selectedOption) return;

        const isCorrect = activePoll.type === 'quiz' 
            ? optionId === activePoll.correctAnswer 
            : undefined;

        const responseData = {
            type: 'poll',
            action: 'STUDENT_RESPONSE',
            studentName: participantName,
            selectedOption: optionId,
            selectedText: selectedOption.text,
            timestamp: Date.now(),
            isCorrect: isCorrect
        };

        try {
            const encoder = new TextEncoder();
            const payload = encoder.encode(JSON.stringify(responseData));
            room.localParticipant?.publishData(payload, { reliable: true });
            
            setMyResponse(optionId);
            toast.success('Response submitted!');
        } catch (error) {
            console.error('Error submitting response:', error);
            toast.error('Failed to submit response');
        }
    };

    const resetForm = () => {
        setFormData({
            question: '',
            type: 'poll',
            options: ['', '', '', ''],
            correctAnswer: ''
        });
    };

    // Student waiting view
    if (userRole === 'student' && isInMeeting && !activePoll) {
        return (
            <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-blue-800 to-indigo-900 rounded-lg">
                <div className="text-center px-6">
                    <div className="mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-blue-400 animate-pulse">
                            <path d="M9 11H3v2h6m4 0h8v-2h-8m2-7h-2v2h2m0 10h-2v2h2"></path>
                            <path d="M3 3v18h18V3z"></path>
                        </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                        Waiting for Poll/Quiz
                    </h3>
                    <p className="text-gray-300 text-sm">
                        The teacher will start a poll or quiz soon
                    </p>
                </div>
            </div>
        );
    }

    // Student active poll/quiz view
    if (userRole === 'student' && isInMeeting && activePoll) {
        return (
            <div className="h-full flex items-center justify-center p-6 bg-gradient-to-br from-blue-900 to-indigo-900">
                <div className="max-w-2xl w-full">
                    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                        {/* Header */}
                        <div className={`p-6 text-white ${
                            activePoll.type === 'poll' 
                                ? 'bg-gradient-to-r from-blue-600 to-indigo-600' 
                                : 'bg-gradient-to-r from-green-600 to-emerald-600'
                        }`}>
                            <div className="flex items-center gap-2 mb-2">
                                <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-semibold uppercase">
                                    {activePoll.type}
                                </span>
                            </div>
                            <h3 className="text-2xl font-bold">{activePoll.question}</h3>
                        </div>

                        {/* Options */}
                        <div className="p-6 space-y-3">
                            {activePoll.options.map((option) => {
                                const isCorrectAnswer = activePoll.type === 'quiz' && option.id === activePoll.correctAnswer;
                                const showAsCorrect = resultsRevealed && isCorrectAnswer;
                                const showAsWrong = resultsRevealed && myResponse === option.id && !isCorrectAnswer && activePoll.type === 'quiz';
                                
                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => !myResponse && !resultsRevealed && submitResponse(option.id)}
                                        disabled={!!myResponse || resultsRevealed}
                                        className={`w-full p-4 rounded-xl text-left transition-all ${
                                            showAsCorrect
                                                ? 'bg-green-100 border-2 border-green-500 shadow-lg'
                                                : showAsWrong
                                                    ? 'bg-red-100 border-2 border-red-500'
                                                    : myResponse === option.id
                                                        ? activePoll.type === 'poll'
                                                            ? 'bg-blue-100 border-2 border-blue-500 shadow-lg'
                                                            : 'bg-green-100 border-2 border-green-500 shadow-lg'
                                                        : 'bg-gray-50 border-2 border-gray-200 hover:border-gray-300 hover:shadow-md'
                                        } ${myResponse && myResponse !== option.id && !showAsCorrect ? 'opacity-50' : ''}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                                    showAsCorrect || (myResponse === option.id && !showAsWrong)
                                                        ? showAsCorrect || activePoll.type === 'quiz'
                                                            ? 'border-green-500 bg-green-500'
                                                            : 'border-blue-500 bg-blue-500'
                                                        : showAsWrong
                                                            ? 'border-red-500 bg-red-500'
                                                            : 'border-gray-300'
                                                }`}>
                                                    {(myResponse === option.id || showAsCorrect) && (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                                            <polyline points="20 6 9 17 4 12"></polyline>
                                                        </svg>
                                                    )}
                                                </div>
                                                <span className="font-medium text-gray-900">{option.text}</span>
                                            </div>
                                            {showAsCorrect && (
                                                <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full font-bold">
                                                    ✓ CORRECT
                                                </span>
                                            )}
                                            {showAsWrong && (
                                                <span className="text-xs bg-red-600 text-white px-2 py-1 rounded-full font-bold">
                                                    ✗ WRONG
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        {myResponse && !resultsRevealed && (
                            <div className="px-6 pb-6">
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                                    <p className="text-green-800 font-semibold">✓ Response submitted!</p>
                                    <p className="text-xs text-green-600 mt-1">Waiting for teacher...</p>
                                </div>
                            </div>
                        )}
                        
                        {/* Results revealed message */}
                        {resultsRevealed && (
                            <div className="px-6 pb-6 space-y-4">
                                <div className={`border-2 rounded-lg p-4 text-center ${
                                    activePoll.type === 'quiz' && myResponse === activePoll.correctAnswer
                                        ? 'bg-green-50 border-green-500'
                                        : activePoll.type === 'quiz'
                                            ? 'bg-red-50 border-red-500'
                                            : 'bg-blue-50 border-blue-500'
                                }`}>
                                    {activePoll.type === 'quiz' ? (
                                        myResponse === activePoll.correctAnswer ? (
                                            <>
                                                <p className="text-green-800 font-bold text-lg">🎉 Correct!</p>
                                                <p className="text-xs text-green-600 mt-1">Great job!</p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-red-800 font-bold text-lg">Incorrect</p>
                                                <p className="text-xs text-red-600 mt-1">The correct answer is highlighted above</p>
                                            </>
                                        )
                                    ) : (
                                        <>
                                            <p className="text-blue-800 font-semibold">Results Revealed</p>
                                            <p className="text-xs text-blue-600 mt-1">Your response has been recorded</p>
                                        </>
                                    )}
                                </div>

                                {/* Results Statistics */}
                                {pollResults && (
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                        <h5 className="font-semibold text-gray-800 mb-3 text-sm">📊 Class Results</h5>
                                        <div className="space-y-2">
                                            {activePoll.options.map((option) => {
                                                const count = pollResults.optionCounts[option.id] || 0;
                                                const percentage = pollResults.totalResponses > 0 ? (count / pollResults.totalResponses) * 100 : 0;
                                                const isCorrect = activePoll.type === 'quiz' && option.id === activePoll.correctAnswer;
                                                const isMyChoice = myResponse === option.id;

                                                return (
                                                    <div key={option.id} className={`p-2 rounded-lg border ${
                                                        isCorrect ? 'border-green-300 bg-green-50' : 'border-gray-200'
                                                    }`}>
                                                        <div className="flex items-center justify-between mb-1">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm font-medium text-gray-900">{option.text}</span>
                                                                {isCorrect && (
                                                                    <span className="text-xs bg-green-500 text-white px-1.5 py-0.5 rounded-full font-bold">
                                                                        ✓ CORRECT
                                                                    </span>
                                                                )}
                                                                {isMyChoice && (
                                                                    <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded-full font-bold">
                                                                        YOUR CHOICE
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className="text-sm font-bold text-gray-700">
                                                                {count} ({percentage.toFixed(0)}%)
                                                            </span>
                                                        </div>
                                                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                            <div 
                                                                className={`h-full transition-all duration-500 ${
                                                                    isCorrect ? 'bg-green-500' : 'bg-blue-500'
                                                                }`}
                                                                style={{ width: `${percentage}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="mt-3 pt-2 border-t border-gray-300 text-xs text-gray-600 text-center">
                                            Total responses: {pollResults.totalResponses}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Teacher view
    if (userRole === 'teacher' && isInMeeting) {
        console.log('📊 Teacher view - activePoll:', activePoll?.id, 'responses:', responses.size);
        
        // Active poll monitoring
        if (activePoll) {
            const totalResponses = responses.size;
            const optionCounts = new Map<string, number>();
            activePoll.options.forEach(opt => optionCounts.set(opt.id, 0));
            
            responses.forEach(response => {
                const current = optionCounts.get(response.selectedOption) || 0;
                optionCounts.set(response.selectedOption, current + 1);
            });

            return (
                <div className="space-y-4 h-full flex flex-col">
                    {/* Header */}
                    <div className={`p-4 rounded-lg text-white flex-shrink-0 ${
                        activePoll.type === 'poll'
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600'
                            : 'bg-gradient-to-r from-green-600 to-emerald-600'
                    }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-bold uppercase">
                                        {activePoll.type} LIVE
                                    </span>
                                    <span className="text-xs">
                                        {totalResponses} responses
                                    </span>
                                </div>
                                <h3 className="text-lg font-bold">{activePoll.question}</h3>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={async () => {
                                        try {
                                            const pollData = {
                                                type: 'poll',
                                                action: 'POLL_STARTED',
                                                poll: activePoll,
                                                timestamp: Date.now()
                                            };
                                            const encoder = new TextEncoder();
                                            const payload = encoder.encode(JSON.stringify(pollData));
                                            await room?.localParticipant?.publishData(payload, { reliable: true });
                                            toast.success('📊 Poll rebroadcasted to all participants!');
                                        } catch (error) {
                                            console.error('Error rebroadcasting poll:', error);
                                            toast.error('Failed to rebroadcast poll');
                                        }
                                    }}
                                    className="px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-semibold transition-colors"
                                    title="Rebroadcast poll to all participants"
                                >
                                    📡 Rebroadcast
                                </button>
                                {!resultsRevealed && (
                                    <button
                                        onClick={revealResults}
                                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                                            activePoll.type === 'quiz'
                                                ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                                                : 'bg-blue-500 hover:bg-blue-600 text-white'
                                        }`}
                                    >
                                        👁️ Reveal {activePoll.type === 'quiz' ? 'Answer' : 'Results'}
                                    </button>
                                )}
                                {resultsRevealed && (
                                    <span className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-semibold">
                                        ✓ Revealed
                                    </span>
                                )}
                                <button
                                    onClick={stopPoll}
                                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold transition-colors"
                                >
                                    ⏹️ Stop
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Results */}
                    <div className="bg-white rounded-lg shadow-lg p-4 flex-1 overflow-auto">
                        <h4 className="font-semibold text-gray-800 mb-4">Live Results</h4>
                        <div className="space-y-3">
                            {activePoll.options.map((option) => {
                                const count = optionCounts.get(option.id) || 0;
                                const percentage = totalResponses > 0 ? (count / totalResponses) * 100 : 0;
                                const isCorrect = activePoll.type === 'quiz' && option.id === activePoll.correctAnswer;

                                return (
                                    <div key={option.id} className={`p-3 rounded-lg border-2 ${
                                        isCorrect ? 'border-green-500 bg-green-50' : 'border-gray-200'
                                    }`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-gray-900">{option.text}</span>
                                                {isCorrect && (
                                                    <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full font-semibold">
                                                        CORRECT
                                                    </span>
                                                )}
                                            </div>
                                            <span className="font-bold text-gray-700">{count} ({percentage.toFixed(0)}%)</span>
                                        </div>
                                        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                                            <div 
                                                className={`h-full transition-all duration-500 ${
                                                    isCorrect ? 'bg-green-500' : 'bg-blue-500'
                                                }`}
                                                style={{ width: `${percentage}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Student list */}
                        {totalResponses > 0 && (
                            <div className="mt-6">
                                <h5 className="font-semibold text-gray-700 text-sm mb-2">Student Responses</h5>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {Array.from(responses.values()).map((response) => {
                                        const option = activePoll.options.find(opt => opt.id === response.selectedOption);
                                        return (
                                            <div key={response.studentName} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                                                <span className="font-medium text-gray-900">{response.studentName}</span>
                                                <span className={`${response.isCorrect ? 'text-green-600 font-semibold' : 'text-gray-600'}`}>
                                                    {option?.text} {response.isCorrect ? '✓' : ''}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // Poll library view (no active poll)
        console.log('📊 Showing poll library - no active poll');
        return (
            <div className="space-y-4">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-bold">📊 Polls & Quizzes</h3>
                            <p className="text-sm text-blue-100">Engage students with interactive polls and quizzes</p>
                            <p className="text-xs text-blue-200">Debug: activePoll={activePoll?.id || 'null'}</p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    console.log('Current state:', { activePoll: activePoll?.id, responses: responses.size, isInMeeting, userRole });
                                    toast.info(`Debug: activePoll=${activePoll?.id || 'null'}, responses=${responses.size}`);
                                }}
                                className="px-3 py-1 bg-yellow-500 text-white rounded text-xs"
                            >
                                Debug
                            </button>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="px-4 py-2 bg-white text-blue-600 hover:bg-blue-50 rounded-lg font-semibold text-sm transition-colors"
                            >
                                + Create New
                            </button>
                        </div>
                    </div>
                </div>

                {/* Saved Polls */}
                <div className="bg-white rounded-lg shadow-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-4">Saved Polls & Quizzes</h4>
                    {savedPolls.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <p className="text-sm">No polls created yet</p>
                            <p className="text-xs mt-2">Create your first poll to get started</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {savedPolls.map((poll) => (
                                <div key={poll.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                    poll.type === 'poll'
                                                        ? 'bg-blue-100 text-blue-700'
                                                        : 'bg-green-100 text-green-700'
                                                }`}>
                                                    {poll.type.toUpperCase()}
                                                </span>
                                            </div>
                                            <h5 className="font-medium text-gray-900 mb-2">{poll.question}</h5>
                                            <div className="text-xs text-gray-500">
                                                {poll.options.length} options
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => startPoll(poll)}
                                            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all"
                                        >
                                            ▶ Start
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Create Modal */}
                {showCreateModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-gray-900">Create Poll/Quiz</h3>
                                    <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M18 6L6 18M6 6l12 12"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <div className="p-6 space-y-4">
                                {/* Type Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => setFormData({ ...formData, type: 'poll', correctAnswer: '' })}
                                            className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                                                formData.type === 'poll'
                                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <div className="font-semibold">📊 Poll</div>
                                            <div className="text-xs mt-1">No correct answer</div>
                                        </button>
                                        <button
                                            onClick={() => setFormData({ ...formData, type: 'quiz' })}
                                            className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                                                formData.type === 'quiz'
                                                    ? 'border-green-500 bg-green-50 text-green-700'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <div className="font-semibold">✅ Quiz</div>
                                            <div className="text-xs mt-1">Has correct answer</div>
                                        </button>
                                    </div>
                                </div>

                                {/* Question */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Question *</label>
                                    <input
                                        type="text"
                                        value={formData.question}
                                        onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                                        placeholder="Enter your question"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                {/* Options */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
                                    <div className="space-y-2">
                                        {formData.options.map((option, idx) => (
                                            <div key={idx} className="flex gap-2">
                                                {formData.type === 'quiz' && (
                                                    <input
                                                        type="radio"
                                                        name="correctAnswer"
                                                        checked={formData.correctAnswer === `opt_${idx}`}
                                                        onChange={() => setFormData({ ...formData, correctAnswer: `opt_${idx}` })}
                                                        className="mt-3 text-green-600 focus:ring-green-500"
                                                    />
                                                )}
                                                <input
                                                    type="text"
                                                    value={option}
                                                    onChange={(e) => {
                                                        const newOptions = [...formData.options];
                                                        newOptions[idx] = e.target.value;
                                                        setFormData({ ...formData, options: newOptions });
                                                    }}
                                                    placeholder={`Option ${idx + 1}`}
                                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    {formData.type === 'quiz' && (
                                        <p className="text-xs text-gray-500 mt-2">
                                            Select the radio button to mark the correct answer
                                        </p>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex gap-3 pt-4">
                                    <button
                                        onClick={() => setShowCreateModal(false)}
                                        className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={createPoll}
                                        className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all"
                                    >
                                        Create & Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Non-meeting view (Subscription Details page)
    return (
        <div className="space-y-4">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-lg">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-xl font-bold mb-2">📊 Polls & Quizzes</h3>
                        <p className="text-sm text-blue-100">
                            Create polls and quizzes to use during live classes. Results are automatically saved.
                        </p>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="px-6 py-3 bg-white text-blue-600 hover:bg-blue-50 rounded-lg font-semibold transition-colors shadow-lg"
                    >
                        + Create New
                    </button>
                </div>
            </div>

            {/* Saved Polls & Quizzes */}
            <div className="bg-white rounded-lg shadow-md">
                <div className="p-6">
                    <h4 className="font-semibold text-gray-800 mb-4 text-lg">Your Polls & Quizzes</h4>
                    {savedPolls.length === 0 ? (
                        <div className="text-center py-16 text-gray-500">
                            <div className="mb-4">
                                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="mx-auto text-gray-300">
                                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                                    <path d="M9 9h6"></path>
                                    <path d="M9 15h6"></path>
                                </svg>
                            </div>
                            <p className="text-base font-medium">No polls or quizzes created yet</p>
                            <p className="text-sm mt-2">Create your first poll or quiz to engage students during live classes</p>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Create Now
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {savedPolls.map((poll) => (
                                <div key={poll.id} className="border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow bg-white">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${
                                                    poll.type === 'poll'
                                                        ? 'bg-blue-100 text-blue-700'
                                                        : 'bg-green-100 text-green-700'
                                                }`}>
                                                    {poll.type}
                                                </span>
                                            </div>
                                            <h5 className="font-semibold text-gray-900 mb-2 text-base">{poll.question}</h5>
                                            <div className="space-y-1 text-sm">
                                                {poll.options.map((opt, idx) => (
                                                    <div key={opt.id} className="flex items-center gap-2 text-gray-600">
                                                        <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                                                            {String.fromCharCode(65 + idx)}
                                                        </span>
                                                        <span className={poll.correctAnswer === opt.id ? 'font-semibold text-green-600' : ''}>
                                                            {opt.text}
                                                            {poll.correctAnswer === opt.id && ' ✓'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Footer with stats and actions */}
                                    <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                                        <div className="text-xs text-gray-500">
                                            <p>
                                                {poll.options.length} options
                                                {poll.type === 'quiz' && ' • Has correct answer'}
                                                {(poll as any).totalResponses > 0 && ` • ${(poll as any).totalResponses} responses`}
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => deletePoll(poll.id)}
                                                disabled={deletingPollId === poll.id}
                                                className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                title="Delete poll/quiz"
                                            >
                                                {deletingPollId === poll.id ? (
                                                    <>
                                                        <svg className="animate-spin inline h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    </>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600 flex-shrink-0 mt-0.5">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="16" x2="12" y2="12"></line>
                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    <div className="text-sm text-blue-900">
                        <p className="font-semibold mb-1">How to use during live classes:</p>
                        <ol className="list-decimal list-inside space-y-1 text-blue-800">
                            <li>Create polls/quizzes here beforehand</li>
                            <li>During a Meet+ session, click the 📊 Polls button</li>
                            <li>Select and start a poll - it broadcasts to all students instantly</li>
                            <li>Monitor responses in real-time</li>
                            <li>Stop poll when done - results automatically saved</li>
                        </ol>
                    </div>
                </div>
            </div>

            {/* Create/Edit Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-900">Create Poll/Quiz</h3>
                                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M18 6L6 18M6 6l12 12"></path>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            {/* Type Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setFormData({ ...formData, type: 'poll', correctAnswer: '' })}
                                        className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                                            formData.type === 'poll'
                                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <div className="font-semibold text-base">📊 Poll</div>
                                        <div className="text-xs mt-1">Opinion poll - no correct answer</div>
                                    </button>
                                    <button
                                        onClick={() => setFormData({ ...formData, type: 'quiz' })}
                                        className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                                            formData.type === 'quiz'
                                                ? 'border-green-500 bg-green-50 text-green-700'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <div className="font-semibold text-base">✅ Quiz</div>
                                        <div className="text-xs mt-1">Quiz question - has correct answer</div>
                                    </button>
                                </div>
                            </div>

                            {/* Question */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Question *</label>
                                <input
                                    type="text"
                                    value={formData.question}
                                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                                    placeholder="Enter your question"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Options */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Options {formData.type === 'quiz' && <span className="text-green-600">(Select correct answer)</span>}
                                </label>
                                <div className="space-y-3">
                                    {formData.options.map((option, idx) => (
                                        <div key={idx} className="flex gap-3 items-center">
                                            {formData.type === 'quiz' && (
                                                <input
                                                    type="radio"
                                                    name="correctAnswer"
                                                    checked={formData.correctAnswer === `opt_${idx}`}
                                                    onChange={() => setFormData({ ...formData, correctAnswer: `opt_${idx}` })}
                                                    className="mt-0 text-green-600 focus:ring-green-500 w-5 h-5"
                                                />
                                            )}
                                            <div className="flex items-center gap-2 flex-1">
                                                <span className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-700">
                                                    {String.fromCharCode(65 + idx)}
                                                </span>
                                                <input
                                                    type="text"
                                                    value={option}
                                                    onChange={(e) => {
                                                        const newOptions = [...formData.options];
                                                        newOptions[idx] = e.target.value;
                                                        setFormData({ ...formData, options: newOptions });
                                                    }}
                                                    placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {formData.type === 'quiz' && !formData.correctAnswer && (
                                    <p className="text-xs text-orange-600 mt-2">
                                        ⚠️ Please select the correct answer using the radio buttons
                                    </p>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={() => setShowCreateModal(false)}
                                    className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={createPoll}
                                    disabled={!formData.question.trim() || (formData.type === 'quiz' && !formData.correctAnswer)}
                                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Create & Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PollsQuizzes;

